/**
 * Logging schemas for structured log entries.
 */

import { z } from 'zod';
import { SafeParseResult } from '../zod-utils';
import {
    type AuthenticatedLogEntry,
    type LogSeverity,
    type LogService,
    type LogException,
    type LogIngestRequest,
    type LogQueryRequest,
    type LogQueryResponse,
    type RequestLogEntry,
    type LogData,
    type LogDataValue,
    type LogDataUnsafe,
    FORBIDDEN_LOG_DATA_KEYS,
} from '../logging.types';

export const LogSeveritySchema: z.ZodType<LogSeverity> = z.union([
    z.literal('DEFAULT'),
    z.literal('INFO'),
    z.literal('NOTICE'),
    z.literal('WARNING'),
    z.literal('ERROR'),
    z.literal('DEBUG'),
    z.literal('CRITICAL'),
    z.literal('ALERT'),
    z.literal('EMERGENCY'),
]);

export const LogServiceSchema: z.ZodType<LogService> = z.union([
    z.literal('pillar-backend'),
    z.literal('pillar-frontend'),
    z.literal('samba-core'),
    z.literal('samba-website'),
    z.literal('samba-sidebar'),
]);

export const LogExceptionSchema: z.ZodType<LogException> = z.lazy(() =>
    z.object({
        name: z.string().min(1),
        message: z.string().min(1),
        stack: z.string().min(1).optional(),
        cause: LogExceptionSchema.optional(),
    })
);

const ForbiddenLogDataKeySet: ReadonlySet<string> = new Set(FORBIDDEN_LOG_DATA_KEYS);

const RawLogDataValueSchema: z.ZodType<LogDataValue> = z.lazy(() =>
    z.union([
        z.string(),
        z.number(),
        z.boolean(),
        z.null(),
        z.array(RawLogDataValueSchema),
        z.record(z.string(), RawLogDataValueSchema),
    ])
);

const RawLogDataRecordSchema: z.ZodType<LogDataUnsafe> = z.record(
    z.string(),
    RawLogDataValueSchema
);

export const LogDataSchema: z.ZodType<LogData> = RawLogDataRecordSchema.superRefine(
    (value: LogDataUnsafe, ctx: z.RefinementCtx) => {
        collectForbiddenLogFields(value, ctx, []);
    }
);

type LogDataPath = Array<string | number>;

function collectForbiddenLogFields(
    value: LogDataValue,
    ctx: z.RefinementCtx,
    path: LogDataPath
): void {
    const arrayParse: SafeParseResult<LogDataValue[]> = z
        .array(RawLogDataValueSchema)
        .safeParse(value);
    if (arrayParse.success) {
        arrayParse.data.forEach((entry: LogDataValue, index: number) => {
            collectForbiddenLogFields(entry, ctx, [...path, index]);
        });
        return;
    }

    const recordParse: SafeParseResult<LogDataUnsafe> = RawLogDataRecordSchema.safeParse(value);
    if (recordParse.success && !Array.isArray(value)) {
        for (const key in recordParse.data) {
            if (!Object.prototype.hasOwnProperty.call(recordParse.data, key)) {
                continue;
            }
            const nextPath: LogDataPath = [...path, key];
            if (ForbiddenLogDataKeySet.has(key)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Forbidden log data field: ${key}`,
                    path: nextPath,
                });
            }
            const nextValueParse: SafeParseResult<LogDataValue> = RawLogDataValueSchema.safeParse(
                recordParse.data[key]
            );
            if (!nextValueParse.success) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Invalid log data value',
                    path: nextPath,
                });
                continue;
            }
            collectForbiddenLogFields(nextValueParse.data, ctx, nextPath);
        }
    }
}

export const RequestLogEntrySchema: z.ZodType<RequestLogEntry> = z
    .object({
        client_timestamp: z.string().datetime().optional(),

        severity: LogSeveritySchema,
        service: LogServiceSchema,
        topics: z.array(z.string().min(1)),
        subsystem: z.string().min(1).optional(),
        workflow_id: z.string().min(1).optional(),
        website_id: z.string().min(1).optional(),
        operation_id: z.string().min(1),
        correlation_id: z.string().min(1).optional(),
        trace_id: z.string().min(1).optional(),

        customer_id: z.string().min(1).optional(),

        message: z.string().min(1),
        data: LogDataSchema.optional(),
        exception: LogExceptionSchema.optional(),
        stack_trace: z.string().min(1).optional(),
    })
    .strip();

export const AuthenticatedLogEntrySchema: z.ZodType<AuthenticatedLogEntry> = z.object({
    timestamp: z.string().datetime(),
    client_timestamp: z.string().datetime().optional(),

    severity: LogSeveritySchema,
    service: LogServiceSchema,
    topics: z.array(z.string().min(1)),
    subsystem: z.string().min(1).optional(),
    workflow_id: z.string().min(1).optional(),
    website_id: z.string().min(1).optional(),
    operation_id: z.string().min(1),
    correlation_id: z.string().min(1).optional(),
    trace_id: z.string().min(1).optional(),

    user_id: z.string().min(1).optional(),
    role_assignment_id: z.string().min(1).optional(),
    audit_id: z.string().min(1).optional(),
    organization_id: z.string().min(1).optional(),
    customer_id: z.string().min(1).optional(),

    message: z.string().min(1),
    data: LogDataSchema.optional(),
    exception: LogExceptionSchema.optional(),
    stack_trace: z.string().min(1).optional(),
});

export const LogIngestRequestSchema: z.ZodType<LogIngestRequest> = z.object({
    entries: z.array(RequestLogEntrySchema),
});

export const LogQueryRequestSchema: z.ZodType<LogQueryRequest> = z.object({
    user_id: z.string().min(1).optional(),
    audit_id: z.string().min(1).optional(),
    role_assignment_id: z.string().min(1).optional(),
    organization_id: z.string().min(1).optional(),
    customer_id: z.string().min(1).optional(),
    trace_id: z.string().min(1).optional(),
    from_timestamp: z.string().datetime().optional(),
    to_timestamp: z.string().datetime().optional(),
    limit: z.coerce.number().int().positive().optional(),
});

export const LogQueryResponseSchema: z.ZodType<LogQueryResponse> = z.object({
    entries: z.array(AuthenticatedLogEntrySchema),
});
